import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Shield, Inbox, Settings as SettingsIcon } from "lucide-react";
import Logo from "@/components/Logo";
import { useAdminAuth } from "@/lib/AdminAuth";
import { toast } from "sonner";

const NAV_ITEMS = [
  { to: "/admin",          label: "Leads",    Icon: Inbox,        end: false, testid: "admin-nav-leads" },
  { to: "/admin/settings", label: "Settings", Icon: SettingsIcon, end: false, testid: "admin-nav-settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-ra-paper text-ra-ink">
      <header className="sticky top-0 z-40 border-b border-ra-line bg-white" data-testid="admin-top-bar">
        <div className="mx-auto flex h-14 max-w-[1400px] items-center justify-between px-5 sm:px-8">
          <div className="flex items-center gap-5">
            <div className="flex items-center gap-3">
              <Logo to="/admin" height={26} />
              <span className="ml-1 inline-flex items-center gap-1 rounded-full border border-ra-line bg-ra-paper px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-ra-ink/55">
                <Shield size={10} /> Admin
              </span>
            </div>
            <nav className="hidden sm:flex items-center gap-1" aria-label="Admin sections">
              {NAV_ITEMS.map(({ to, label, Icon, end, testid }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  data-testid={testid}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                      isActive
                        ? "bg-ra-ocean/15 text-ra-oceanDark"
                        : "text-ra-ink/65 hover:bg-ra-paper hover:text-ra-ink"
                    }`
                  }
                >
                  <Icon size={12} /> {label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Link
              to="/"
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline text-xs font-medium text-ra-ink/55 hover:text-ra-ink"
              data-testid="admin-view-site-link"
            >
              View site ↗
            </Link>
            {user && (
              <span className="hidden md:inline text-xs text-ra-ink/65" data-testid="admin-current-user">
                {user.email}
              </span>
            )}
            <button
              type="button"
              onClick={onLogout}
              className="inline-flex items-center gap-1.5 rounded-full border border-ra-line bg-white px-3 py-1.5 text-xs font-semibold text-ra-ink hover:border-ra-ocean hover:text-ra-ocean transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1400px] px-5 sm:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
}
